// components/brvm/BrvmChart.jsx — Graphique zone interactif (SVG pur, données réelles).
import { useRef, useState } from "react";

const W = 640;
const H = 190;
const PAD = { t: 10, r: 8, b: 22, l: 44 };

function _fmtAxis(v) {
  return Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 2 });
}

function _fmtTs(iso, period) {
  const d = new Date(iso);
  if (period === "1J") return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

export default function BrvmChart({ points = [], period = "1J", color = "#34D399" }) {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);

  if (!points || points.length < 2) {
    return (
      <div data-testid="brvm-chart-empty" style={{ height: 150, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, color: "#6B7F72", fontSize: 12 }}>
        <span style={{ fontSize: 20 }}>📈</span>
        <span>Historique en construction — les points s'accumulent à chaque séance.</span>
      </div>
    );
  }

  const vals = points.map((p) => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || max * 0.01 || 1;
  const x = (i) => PAD.l + (i / (points.length - 1)) * (W - PAD.l - PAD.r);
  const y = (v) => PAD.t + (1 - (v - min) / span) * (H - PAD.t - PAD.b);
  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ");
  const gridVals = [min, min + span / 3, min + (2 * span) / 3, max];

  const onMove = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = ((e.clientX ?? e.touches?.[0]?.clientX) - rect.left) / rect.width * W;
    const ratio = Math.min(1, Math.max(0, (px - PAD.l) / (W - PAD.l - PAD.r)));
    setHover(Math.round(ratio * (points.length - 1)));
  };

  const hp = hover != null ? points[hover] : null;

  return (
    <div style={{ position: "relative" }} data-testid="brvm-chart">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block", touchAction: "pan-y" }}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="brvm-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.30" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {gridVals.map((v, i) => (
          <g key={i}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} stroke="rgba(207,229,214,0.10)" strokeWidth="1" />
            <text x={PAD.l - 6} y={y(v) + 3.5} textAnchor="end" fontSize="10" fill="#6B7F72">{_fmtAxis(v)}</text>
          </g>
        ))}
        <path d={`${line} L${x(points.length - 1)},${H - PAD.b} L${PAD.l},${H - PAD.b} Z`} fill="url(#brvm-area)" />
        <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {[0, Math.floor((points.length - 1) / 2), points.length - 1].map((i, k) => (
          <text key={k} x={x(i)} y={H - 6} textAnchor={k === 0 ? "start" : k === 2 ? "end" : "middle"} fontSize="10" fill="#6B7F72">
            {_fmtTs(points[i].ts, period)}
          </text>
        ))}
        {hp && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={PAD.t} y2={H - PAD.b} stroke="rgba(236,245,238,0.35)" strokeDasharray="3 3" />
            <circle cx={x(hover)} cy={y(hp.value)} r="4" fill={color} stroke="#04120B" strokeWidth="1.5" />
          </g>
        )}
      </svg>
      {hp && (
        <div style={{
          position: "absolute", top: 2, left: "50%", transform: "translateX(-50%)",
          padding: "3px 10px", borderRadius: 999, background: "rgba(4,18,11,0.92)",
          border: "1px solid rgba(16,185,129,0.4)", fontSize: 11, fontWeight: 700, color: "#ECF5EE", whiteSpace: "nowrap",
        }}>
          {_fmtAxis(hp.value)} · {_fmtTs(hp.ts, period)}
        </div>
      )}
    </div>
  );
}
